import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { getCompany, getUser } from "../../data/storage/authStorage";

const STORAGE_PREFIX = "zournal.state";

const getStorageScope = (): string => {
  try {
    const user = getUser();
    const company = getCompany();
    const userId = user?.id || "anon";
    const companyId = company?.companyId || "no-company";
    return `${userId}.${companyId}`;
  } catch {
    return "default";
  }
};

const buildKey = (sectionKey: string): string =>
  `${STORAGE_PREFIX}.${getStorageScope()}.${sectionKey}`;

interface PersistedStateOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (raw: string, fallback: T) => T;
}

const resolveInitialValue = <T>(initialValue: T | (() => T)): T =>
  typeof initialValue === "function"
    ? (initialValue as () => T)()
    : initialValue;

const readPersistedState = <T>(
  sectionKey: string,
  fallback: T,
  deserialize?: (raw: string, fallback: T) => T,
): T => {
  try {
    const raw = localStorage.getItem(buildKey(sectionKey));
    if (!raw) return fallback;
    return deserialize ? deserialize(raw, fallback) : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
};

/**
 * Writes a persisted-state value without mounting the hook.
 *
 * Added for the v1 Home dashboard (issue #636): its task rows need to open a
 * section already filtered ("3 invoices on hold" -> the Invoices list on its
 * On hold tab). Those sections keep their filter in persisted state rather than
 * in the URL, and they read it in their `useState` initializer, so setting the
 * value immediately before navigating is what makes the deep link land.
 *
 * Purely additive — `usePersistedState` below is unchanged and both go through
 * the same `buildKey`, so the storage scope can never diverge.
 */
export const writePersistedState = <T>(
  sectionKey: string,
  value: T,
  serialize?: (value: T) => string,
): void => {
  try {
    localStorage.setItem(
      buildKey(sectionKey),
      serialize ? serialize(value) : JSON.stringify(value),
    );
  } catch {
    // Ignore storage errors (quota exceeded, private mode, disabled storage)
  }
};

export const usePersistedState = <T>(
  sectionKey: string,
  initialValue: T | (() => T),
  options: PersistedStateOptions<T> = {},
): [T, Dispatch<SetStateAction<T>>] => {
  const { deserialize, serialize } = options;
  const [state, setState] = useState<T>(() => {
    const fallback = resolveInitialValue(initialValue);
    return readPersistedState(sectionKey, fallback, deserialize);
  });

  const setPersistedState = useCallback<Dispatch<SetStateAction<T>>>(
    (nextValue) => {
      setState((previousValue) => {
        const next =
          typeof nextValue === "function"
            ? (nextValue as (value: T) => T)(previousValue)
            : nextValue;

        try {
          localStorage.setItem(
            buildKey(sectionKey),
            serialize ? serialize(next) : JSON.stringify(next),
          );
        } catch {
          // Ignore storage errors (quota exceeded, private mode, disabled storage)
        }

        return next;
      });
    },
    [sectionKey, serialize],
  );

  return [state, setPersistedState];
};
